// Overridable clock. All *user-visible* "now" reads route through here so an
// admin can pin the app to an arbitrary date/time and preview how time-dependent
// UI (active-event filtering, the schedule now-line, countdowns) renders at any
// moment of a festival — without waiting for the real date.
//
// Non-display reads (cache TTLs, OTA cache-busting, perf telemetry, logger
// timestamps) must keep using real wall time and intentionally do NOT use this.

// Time-advance simulation: while active, the override clock fast-forwards at
// SIM_RATE simulated seconds per real second (5 minutes every real second), so a
// tester can speed-run time-dependent UI (the now-line crossing sets, countdown
// expiries, active→past transitions) without waiting. In-memory only — a reload
// stops it. Driven off real elapsed time so interval jitter / tab-throttling
// can't drift the rate.
//
// We tick every SIM_TICK_MS and snap the result to whole simulated minutes, so
// the clock advances one minute at a time (at 5 min/sec that's a 1-minute step
// every 200ms) rather than leaping in larger jumps — the now-line crawls forward
// instead of teleporting.
const SIM_RATE = 300
const SIM_TICK_MS = 200
// Dead-air boost: the festival preview bumps the rate while the clock is between
// sets (after a day's last set, before the next day begins) so the empty hours
// fly by instead of crawling. Callers toggle it with setSimFastForward.
const SIM_FAST_RATE = 6000

// Persisted so an override survives a reload ("this browser only" per the
// admin clock page copy) and so tooling (E2E specs) can seed it via
// localStorage before the app boots.
const STORAGE_KEY = 'beacon_clock_override'

function readStoredOverride(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const millis = new Date(JSON.parse(raw)).getTime()
    return Number.isNaN(millis) ? null : millis
  } catch {
    return null
  }
}

function writeStoredOverride(date: Date | null): void {
  try {
    if (date === null) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(date.toISOString()))
  } catch {
    // Storage unavailable (private browsing, etc.) — override still works in-memory.
  }
}

let overrideMillis: number | null = readStoredOverride()
const subscribers = new Set<() => void>()

let simulating = false
let simTimer: ReturnType<typeof setInterval> | null = null
let simBaseMillis = 0
let simRealStart = 0
let simRate = SIM_RATE

function notify(): void {
  for (const fn of subscribers) fn()
}

/** Current effective time as a Date — the override if set, otherwise real now. */
export function getNow(): Date {
  return new Date(overrideMillis ?? Date.now())
}

/** Current effective time in epoch millis. */
export function nowMillis(): number {
  return overrideMillis ?? Date.now()
}

/** The active override, or null when running on live time. */
export function getClockOverride(): Date | null {
  return overrideMillis === null ? null : new Date(overrideMillis)
}

/** Set (or clear, with null) the override. In-memory only — resets on restart. */
export function setClockOverride(date: Date | null): void {
  overrideMillis = date === null ? null : date.getTime()
  writeStoredOverride(date)
  if (date === null) {
    // Returning to live time also ends any running simulation.
    stopSim()
  } else if (simulating) {
    // Re-anchor the fast-forward to the newly set instant (e.g. the user typed a
    // time, or dragged the now-line) and keep advancing from there.
    simBaseMillis = overrideMillis!
    simRealStart = Date.now()
  }
  notify()
}

/** Whether the time-advance simulation is currently running. */
export function isSimulating(): boolean {
  return simulating
}

function stopSim(): void {
  simulating = false
  if (simTimer) {
    clearInterval(simTimer)
    simTimer = null
  }
}

/** Start/stop the fast-forward simulation (5 minutes per real second). */
export function setSimulating(on: boolean): void {
  if (on === simulating) return
  if (on) {
    // Anchor to the current effective instant so it ticks on from "here".
    simBaseMillis = overrideMillis ?? Date.now()
    simRealStart = Date.now()
    overrideMillis = simBaseMillis
    simRate = SIM_RATE
    simulating = true
    simTimer = setInterval(() => {
      const elapsedSimMs = (Date.now() - simRealStart) * simRate
      const wholeMinutes = Math.floor(elapsedSimMs / 60_000)
      const next = simBaseMillis + wholeMinutes * 60_000
      if (next === overrideMillis) return // same minute — skip the redundant notify
      overrideMillis = next
      notify()
    }, SIM_TICK_MS)
  } else {
    stopSim()
  }
  notify()
}

/**
 * Toggle the dead-air boost on a running simulation. Re-anchors so the speed
 * change is continuous (no time jump). No-op when not simulating, or when the
 * rate is already at the requested setting.
 */
export function setSimFastForward(on: boolean): void {
  const target = on ? SIM_FAST_RATE : SIM_RATE
  if (!simulating || target === simRate) return
  simBaseMillis = overrideMillis ?? Date.now()
  simRealStart = Date.now()
  simRate = target
}

/** Subscribe to override changes. Returns an unsubscribe function. */
export function subscribe(fn: () => void): () => void {
  subscribers.add(fn)
  return () => subscribers.delete(fn)
}
